import { Heading, LuIcon, Separator } from "@overmap-ai/blocks"
import { type AssetTypeAttachment, OrganizationAccessLevel, type Stored } from "@overmap-ai/common"
import { selectAssetTypeById, selectAttachmentsOfAssetType } from "@overmap-ai/core"
import { getRouteApi } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { AttachmentList } from "../../components/AttachmentList/AttachmentList"
import { AttachmentButton } from "../../components/Buttons/AttachmentButton"
import { ViewContainer } from "../../components/Containers/ViewContainer"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/asset-types/$assetTypeId/attachments")

export const AssetTypeSettingsAttachmentsView = memo(() => {
	const { assetTypeId } = routeApi.useParams()
	const assetType = useAppSelector(selectAssetTypeById(assetTypeId))

	if (!assetType) {
		throw new Error(`expected assetType to exist, got ${assetType}.`)
	}

	const attachments = useShallowEqaulSelector(selectAttachmentsOfAssetType(assetType.uuid))
	const openConfirmDeleteDialog = useConfirmDeleteDialog()

	const handleAttachFiles = useCallback(
		(files: File[]) => {
			return sdk.assetTypeAttachments.bulkAdd(
				files.map((file) => ({
					asset_type: assetType.uuid,
					file,
				})),
			)
		},
		[assetType.uuid],
	)

	const handleDeleteAttachment = useCallback(
		(fileAttachment: Stored<AssetTypeAttachment>) => {
			openConfirmDeleteDialog({
				title: "Delete attachment",
				description: "The attachment will be deleted immediately. This action cannot be undone.",
				onAction: () => sdk.assetTypeAttachments.delete(fileAttachment.uuid),
			})
		},
		[openConfirmDeleteDialog],
	)

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4">
				<div className="flex items-center justify-between gap-2">
					<Heading size="xl">Attachments</Heading>

					<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
						<AttachmentButton size="sm" variant="surface" onAttachFiles={handleAttachFiles}>
							<LuIcon icon={modelIconMapping.add} />
							Add
						</AttachmentButton>
					</OrganizationAccessRequired>
				</div>

				<Separator size="full" />

				<AttachmentList
					attachments={attachments}
					onDelete={handleDeleteAttachment}
					service={sdk.assetTypeAttachments}
				/>
			</section>
		</ViewContainer>
	)
})
AssetTypeSettingsAttachmentsView.displayName = "AssetTypeSettingsAttachmentsView"
